import { useState, useEffect } from 'react'
import { Plus, Search, MoreHorizontal, Edit, Trash2, Eye, Shield, UserCheck, Building } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useToast } from '@/hooks/use-toast'
import { supabase } from '@/integrations/supabase/client'
import { auditLogger } from '@/utils/auditLogger'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { validateEmail, validateName } from '@/utils/inputValidation'

interface UserData {
  id: string
  email: string
  created_at: string
  email_confirmed_at?: string
  last_sign_in_at?: string
  profile?: {
    full_name?: string
  }
  role?: {
    role: 'superadmin' | 'admin' | 'customer'
  }
  tenant?: {
    name: string
    id: string
  }
}

const UserManagement = () => {
  const [users, setUsers] = useState<UserData[]>([])
  const [filteredUsers, setFilteredUsers] = useState<UserData[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [roleFilter, setRoleFilter] = useState<string>('all')
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false)
  const [selectedUser, setSelectedUser] = useState<UserData | null>(null)
  const [editRole, setEditRole] = useState<'superadmin' | 'admin' | 'customer'>('customer')
  const [isAssignTenantModalOpen, setIsAssignTenantModalOpen] = useState(false)
  const [availableTenants, setAvailableTenants] = useState<{id: string, name: string}[]>([])
  const [selectedTenantId, setSelectedTenantId] = useState<string>('')
  const [errors, setErrors] = useState<Record<string, string>>({})
  const { toast } = useToast()

  useEffect(() => {
    fetchUsers()
  }, [])

  useEffect(() => {
    // Filter users based on search term and role
    let filtered = users
    
    if (searchTerm.trim() !== '') {
      filtered = filtered.filter(user =>
        user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.profile?.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.tenant?.name?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    }
    
    if (roleFilter !== 'all') {
      filtered = filtered.filter(user => user.role?.role === roleFilter)
    }
    
    setFilteredUsers(filtered)
  }, [users, searchTerm, roleFilter])

  const fetchUsers = async () => {
    try {
      setLoading(true)
      console.log('🔍 Fetching users for UserManagement...')
      
      // Start with all users that have roles instead of profiles
      const { data: userRoles, error: rolesError } = await supabase
        .from('user_roles')
        .select('user_id, role')
        .order('created_at', { ascending: false })

      console.log('🎭 User roles query result:', { userRoles, rolesError })

      if (rolesError) {
        console.error('❌ Error fetching user roles:', rolesError)
        throw rolesError
      }

      if (!userRoles?.length) {
        console.log('📭 No user roles found')
        setUsers([])
        return
      }

      // Get profiles for these users
      const userIds = userRoles.map(ur => ur.user_id)
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('user_id, email, full_name, created_at, updated_at')
        .in('user_id', userIds)

      console.log('📊 Profiles query result:', { profiles, profilesError })

      if (profilesError) {
        console.error('❌ Error fetching profiles:', profilesError)
        throw profilesError
      }

      // Create profile lookup map
      const profileMap: Record<string, any> = {}
      profiles?.forEach(profile => {
        profileMap[profile.user_id] = profile
      })

      // Get tenant information for admins
      const adminUserIds = userRoles?.filter(ur => ur.role === 'admin').map(ur => ur.user_id) || []
      let tenantInfo: Record<string, { name: string; id: string }> = {}
      
      if (adminUserIds.length > 0) {
        const { data: tenants, error: tenantsError } = await supabase
          .from('tenants')
          .select('id, name, admin_user_id')
          .in('admin_user_id', adminUserIds)
        
        console.log('🏢 Tenant data for admins:', { tenants, tenantsError })
        
        if (!tenantsError && tenants) {
          tenants.forEach(tenant => {
            if (tenant.admin_user_id) {
              tenantInfo[tenant.admin_user_id] = {
                name: tenant.name,
                id: tenant.id
              }
            }
          })
        }
      }

      // Get tenant information for customers
      const customerUserIds = userRoles?.filter(ur => ur.role === 'customer').map(ur => ur.user_id) || []
      
      if (customerUserIds.length > 0) {
        const { data: customers, error: customersError } = await supabase
          .from('customers')
          .select(`
            user_id,
            tenant_id,
            tenants!inner(id, name)
          `)
          .in('user_id', customerUserIds)
          .not('tenant_id', 'is', null)
        
        console.log('👥 Customer tenant data:', { customers, customersError })
        
        if (!customersError && customers) {
          customers.forEach(customer => {
            if (customer.user_id && customer.tenants) {
              tenantInfo[customer.user_id] = {
                name: customer.tenants.name,
                id: customer.tenants.id
              }
            }
          })
        }
      }

      // Transform data to match UserData interface - now based on roles, not profiles
      const userData: UserData[] = []
      
      for (const userRole of userRoles) {
        const profile = profileMap[userRole.user_id]
        
        // If profile is missing, try to create it automatically
        if (!profile) {
          console.log(`🚨 Missing profile detected for user ${userRole.user_id} (${userRole.role}). Attempting to create...`)
          
          try {
            // Create missing profile
            await supabase.rpc('create_missing_profile', {
              target_user_id: userRole.user_id,
              fallback_email: `${userRole.role}-${userRole.user_id.substring(0, 8)}@system.local`
            })
            
            // Fetch the newly created profile
            const { data: newProfile } = await supabase
              .from('profiles')
              .select('user_id, email, full_name, created_at, updated_at')
              .eq('user_id', userRole.user_id)
              .single()
            
            if (newProfile) {
              profileMap[userRole.user_id] = newProfile
              console.log('✅ Successfully created missing profile for user:', userRole.user_id)
            }
          } catch (profileError) {
            console.error('❌ Failed to create missing profile:', profileError)
          }
        }
        
        const finalProfile = profileMap[userRole.user_id]
        
        userData.push({
          id: userRole.user_id,
          email: finalProfile?.email || `${userRole.role}@system.local`,
          created_at: finalProfile?.created_at || new Date().toISOString(),
          profile: {
            full_name: finalProfile?.full_name || undefined
          },
          role: {
            role: userRole.role as any
          },
          tenant: tenantInfo[userRole.user_id] || undefined
        })
      }

      console.log('✅ Successfully transformed user data:', userData.length, 'users')
      setUsers(userData)
      await auditLogger.logDataAccess('users', undefined, 'view')
    } catch (error: any) {
      console.error('❌ Error in fetchUsers:', error)
      toast({
        variant: "destructive",
        title: "Fehler beim Laden",
        description: `Benutzer konnten nicht geladen werden: ${error.message}`
      })
    } finally {
      setLoading(false)
    }
  }

  const fetchAvailableTenants = async () => {
    try {
      const { data: tenants, error } = await supabase
        .from('tenants')
        .select('id, name')
        .eq('status', 'active')
        .order('name')
      
      if (error) throw error
      setAvailableTenants(tenants || [])
    } catch (error: any) {
      console.error('Error fetching tenants:', error)
      toast({
        variant: "destructive",
        title: "Fehler beim Laden",
        description: "Unternehmen konnten nicht geladen werden."
      })
    }
  }

  const assignUserToTenant = async () => {
    if (!selectedUser || !selectedTenantId) return

    try {
      setLoading(true)

      // Update customer tenant assignment
      const { error } = await supabase
        .from('customers')
        .update({ 
          tenant_id: selectedTenantId === 'none' ? null : selectedTenantId,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', selectedUser.id)

      if (error) throw error

      await auditLogger.logAdminAction('assign_customer_to_tenant', 'customers', selectedUser.id, {
        tenant_id: selectedTenantId === 'none' ? null : selectedTenantId,
        user_email: selectedUser.email
      })

      toast({
        title: selectedTenantId === 'none' ? "Zuweisung entfernt" : "Unternehmen zugewiesen",
        description: selectedTenantId === 'none' 
          ? "Benutzer wurde von Unternehmen entfernt."
          : `Benutzer wurde erfolgreich zugewiesen.`
      })

      setIsAssignTenantModalOpen(false)
      setSelectedUser(null)
      setSelectedTenantId('')
      fetchUsers()

    } catch (error: any) {
      console.error('Error assigning user to tenant:', error)
      toast({
        variant: "destructive",
        title: "Fehler beim Zuweisen",
        description: error.message || "Die Zuweisung konnte nicht durchgeführt werden."
      })
    } finally {
      setLoading(false)
    }
  }

  const updateUserRole = async (userId: string, newRole: 'superadmin' | 'admin' | 'customer') => {
    try {
      setLoading(true)
      
      const { error } = await supabase
        .from('user_roles')
        .update({ role: newRole })
        .eq('user_id', userId)

      if (error) throw error

      await auditLogger.logAdminAction('update_user_role', 'user_roles', userId, {
        new_role: newRole,
        user_email: selectedUser?.email
      })

      toast({
        title: "Rolle aktualisiert",
        description: `Benutzerrolle wurde erfolgreich zu ${getRoleLabel(newRole)} geändert.`
      })

      setIsEditModalOpen(false)
      setSelectedUser(null)
      fetchUsers()

    } catch (error: any) {
      console.error('Error updating user role:', error)
      toast({
        variant: "destructive",
        title: "Fehler beim Aktualisieren",
        description: error.message || "Die Rolle konnte nicht aktualisiert werden."
      })
    } finally {
      setLoading(false)
    }
  }

  const deleteUser = async (userId: string, userEmail: string) => {
    if (!confirm(`Sind Sie sicher, dass Sie den Benutzer ${userEmail} löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden.`)) {
      return
    }

    try {
      setLoading(true)
      console.log('🗑️ Attempting to delete user:', userId, userEmail)
      
      // For demo purposes, we'll just mark as deleted in our system
      // In production, you'd need an edge function with service role access
      
      // First, remove user roles
      const { error: roleError } = await supabase
        .from('user_roles')
        .delete()
        .eq('user_id', userId)
      
      if (roleError) {
        console.error('Error deleting user roles:', roleError)
      }

      // Remove from profiles (this should cascade from auth delete in production)
      const { error: profileError } = await supabase
        .from('profiles')
        .delete()
        .eq('user_id', userId)

      if (profileError) {
        console.error('Error deleting profile:', profileError)
        throw new Error('Benutzer konnte nicht vollständig gelöscht werden')
      }

      await auditLogger.logAdminAction('delete_user', 'profiles', userId, {
        user_email: userEmail
      })

      toast({
        title: "Benutzer gelöscht",
        description: `${userEmail} wurde aus dem System entfernt.`
      })

      fetchUsers()

    } catch (error: any) {
      console.error('Error deleting user:', error)
      toast({
        variant: "destructive",
        title: "Fehler beim Löschen",
        description: error.message || "Der Benutzer konnte nicht gelöscht werden."
      })
    } finally {
      setLoading(false)
    }
  }

  const openDetailsModal = (user: UserData) => {
    setSelectedUser(user)
    setIsDetailsModalOpen(true)
  }

  const getRoleBadge = (role: string) => {
    const variants = {
      superadmin: 'bg-red-100 text-red-800 hover:bg-red-100',
      admin: 'bg-blue-100 text-blue-800 hover:bg-blue-100',
      customer: 'bg-green-100 text-green-800 hover:bg-green-100'
    }
    
    const labels = {
      superadmin: 'Superadmin',
      admin: 'Administrator',
      customer: 'Kunde'
    }

    return (
      <Badge className={variants[role as keyof typeof variants] || variants.customer}>
        {labels[role as keyof typeof labels] || 'Unbekannt'}
      </Badge>
    )
  }

  const getRoleLabel = (role: string) => {
    const labels = {
      superadmin: 'Superadmin',
      admin: 'Administrator', 
      customer: 'Kunde'
    }
    return labels[role as keyof typeof labels] || 'Unbekannt'
  }

  const formatDate = (timestamp: string) => {
    return new Date(timestamp).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const openEditModal = (user: UserData) => {
    setSelectedUser(user)
    setEditRole(user.role?.role || 'customer')
    setIsEditModalOpen(true)
  }

  const openAssignTenantModal = (user: UserData) => {
    setSelectedUser(user)
    setSelectedTenantId(user.tenant?.id || '')
    fetchAvailableTenants()
    setIsAssignTenantModalOpen(true)
  }

  const getUserStats = () => {
    const totalUsers = users.length
    const superadmins = users.filter(u => u.role?.role === 'superadmin').length
    const admins = users.filter(u => u.role?.role === 'admin').length
    const customers = users.filter(u => u.role?.role === 'customer').length
    
    return { totalUsers, superadmins, admins, customers }
  }

  // Cleanup function for existing problems
  const handleCleanupSuperadminCustomers = async () => {
    if (!window.confirm('Möchten Sie wirklich die Bereinigung der Superadmin-Kunden durchführen? Dies wird Superadmins aus der Kundentabelle entfernen und fehlende Profile erstellen.')) {
      return
    }

    setLoading(true)
    try {
      console.log('🧹 Starting superadmin cleanup...')
      
      const { error } = await supabase.rpc('cleanup_superadmin_customers')
      
      if (error) {
        throw error
      }
      
      toast({
        title: "Bereinigung erfolgreich",
        description: "Superadmin-Kunden wurden bereinigt und fehlende Profile erstellt."
      })
      
      // Refresh user list
      await fetchUsers()
      
    } catch (error: any) {
      console.error('❌ Error during cleanup:', error)
      toast({
        variant: "destructive",
        title: "Fehler bei der Bereinigung",
        description: error.message || "Die Bereinigung konnte nicht durchgeführt werden."
      })
    } finally {
      setLoading(false)
    }
  }

  const stats = getUserStats()

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-bold tracking-tight">Benutzer verwalten</h2>
            <p className="text-muted-foreground">
              Verwalten Sie alle Benutzer und deren Rollen im System
            </p>
          </div>
          <Button 
            onClick={handleCleanupSuperadminCustomers}
            variant="outline"
            className="gap-2"
            disabled={loading}
          >
            <Shield className="h-4 w-4" />
            Bereinigung durchführen
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid gap-4 md:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Gesamt</CardTitle>
              <UserCheck className="h-4 w-4 text-gray-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.totalUsers}</div>
              <p className="text-xs text-muted-foreground">Alle Benutzer</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Superadmins</CardTitle>
              <Shield className="h-4 w-4 text-red-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.superadmins}</div>
              <p className="text-xs text-muted-foreground">System-Administratoren</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Admins</CardTitle>
              <UserCheck className="h-4 w-4 text-blue-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.admins}</div>
              <p className="text-xs text-muted-foreground">Unternehmens-Admins</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Kunden</CardTitle>
              <UserCheck className="h-4 w-4 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.customers}</div>
              <p className="text-xs text-muted-foreground">End-Kunden</p>
            </CardContent>
          </Card>
        </div>

        {/* Search and Filter */}
        <Card>
          <CardHeader>
            <CardTitle>Benutzer ({filteredUsers.length})</CardTitle>
            <CardDescription>
              Übersicht aller Benutzer im System
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col sm:flex-row gap-4 mb-4">
              <div className="flex items-center space-x-2 flex-1">
                <Search className="h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Suchen nach E-Mail, Name oder Unternehmen..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="flex-1"
                />
              </div>
              
              <Select value={roleFilter} onValueChange={setRoleFilter}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Rolle filtern" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle Rollen</SelectItem>
                  <SelectItem value="superadmin">Superadmin</SelectItem>
                  <SelectItem value="admin">Administrator</SelectItem>
                  <SelectItem value="customer">Kunde</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Benutzer</TableHead>
                    <TableHead>Rolle</TableHead>
                    <TableHead>Unternehmen</TableHead>
                    <TableHead>Registriert</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Aktionen</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8">
                        Lade Benutzer...
                      </TableCell>
                    </TableRow>
                  ) : filteredUsers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                        {searchTerm || roleFilter !== 'all' ? 'Keine Benutzer gefunden.' : 'Noch keine Benutzer vorhanden.'}
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredUsers.map((user) => (
                      <TableRow key={user.id}>
                        <TableCell>
                          <div>
                            <div className="font-medium">{user.email}</div>
                            {user.profile?.full_name && (
                              <div className="text-sm text-muted-foreground">{user.profile.full_name}</div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>{getRoleBadge(user.role?.role || 'customer')}</TableCell>
                        <TableCell>
                          {user.tenant?.name || (
                            <span className="text-muted-foreground text-sm">Kein Unternehmen</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {formatDate(user.created_at)}
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary">
                            Aktiv
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" className="h-8 w-8 p-0">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => openEditModal(user)}>
                                <Edit className="mr-2 h-4 w-4" />
                                Rolle ändern
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => openDetailsModal(user)}>
                                <Eye className="mr-2 h-4 w-4" />
                                Details anzeigen
                              </DropdownMenuItem>
                              {user.role?.role === 'customer' && (
                                <DropdownMenuItem onClick={() => openAssignTenantModal(user)}>
                                  <Building className="mr-2 h-4 w-4" />
                                  Unternehmen zuweisen
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuItem 
                                className="text-destructive"
                                onClick={() => deleteUser(user.id, user.email)}
                              >
                                <Trash2 className="mr-2 h-4 w-4" />
                                Löschen
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        {/* Details Modal */}
        <Dialog open={isDetailsModalOpen} onOpenChange={setIsDetailsModalOpen}>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle>Benutzer Details</DialogTitle>
              <DialogDescription>
                Detaillierte Informationen über {selectedUser?.email}
              </DialogDescription>
            </DialogHeader>
            
            {selectedUser && (
              <div className="grid gap-4 py-4">
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Benutzer-ID</Label>
                    <p className="text-sm text-muted-foreground font-mono break-all">
                      {selectedUser.id}
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">E-Mail</Label>
                    <p className="text-sm">{selectedUser.email}</p>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Name</Label>
                    <p className="text-sm">
                      {selectedUser.profile?.full_name || 'Nicht angegeben'}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Rolle</Label>
                    <div>{getRoleBadge(selectedUser.role?.role || 'customer')}</div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Unternehmen</Label>
                    <p className="text-sm">
                      {selectedUser.tenant?.name || (
                        <span className="text-muted-foreground">Kein Unternehmen zugeordnet</span>
                      )}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Registriert am</Label>
                    <p className="text-sm text-muted-foreground">
                      {formatDate(selectedUser.created_at)}
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Status</Label>
                    <Badge variant="secondary">Aktiv</Badge>
                  </div>
                </div>

                {selectedUser.tenant && (
                  <div className="p-3 bg-muted rounded-lg">
                    <Label className="text-sm font-medium">Unternehmen Details</Label>
                    <div className="mt-2 space-y-1">
                      <p className="text-sm">
                        <span className="font-medium">Name:</span> {selectedUser.tenant.name}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        <span className="font-medium">ID:</span> {selectedUser.tenant.id}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsDetailsModalOpen(false)}>
                Schließen
              </Button>
              {selectedUser && (
                <Button onClick={() => {
                  setIsDetailsModalOpen(false)
                  openEditModal(selectedUser)
                }}>
                  Rolle bearbeiten
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Role Modal */}
        <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
          <DialogContent className="sm:max-w-[400px]">
            <DialogHeader>
              <DialogTitle>Benutzerrolle ändern</DialogTitle>
              <DialogDescription>
                Ändern Sie die Rolle von {selectedUser?.email}
              </DialogDescription>
            </DialogHeader>
            
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="role">Neue Rolle</Label>
                <Select value={editRole} onValueChange={(value: 'superadmin' | 'admin' | 'customer') => setEditRole(value)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="customer">Kunde</SelectItem>
                    <SelectItem value="admin">Administrator</SelectItem>
                    <SelectItem value="superadmin">Superadmin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="bg-muted p-3 rounded-md text-sm">
                <p className="font-medium mb-1">Rollenbeschreibungen:</p>
                <ul className="space-y-1 text-muted-foreground">
                  <li><strong>Kunde:</strong> Kann eigene Stempelkarten verwenden</li>
                  <li><strong>Administrator:</strong> Kann Unternehmen verwalten</li>
                  <li><strong>Superadmin:</strong> Vollzugriff auf das System</li>
                </ul>
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsEditModalOpen(false)}>
                Abbrechen
              </Button>
              <Button onClick={() => selectedUser && updateUserRole(selectedUser.id, editRole)} disabled={loading}>
                {loading ? 'Aktualisiere...' : 'Rolle ändern'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Assign Tenant Modal */}
        <Dialog open={isAssignTenantModalOpen} onOpenChange={setIsAssignTenantModalOpen}>
          <DialogContent className="sm:max-w-[425px]">
            <DialogHeader>
              <DialogTitle>Unternehmen zuweisen</DialogTitle>
              <DialogDescription>
                Weisen Sie dem Benutzer ein Unternehmen zu oder entfernen Sie die aktuelle Zuweisung.
              </DialogDescription>
            </DialogHeader>
            
            {selectedUser && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Benutzer</Label>
                  <p className="text-sm text-muted-foreground">{selectedUser.email}</p>
                </div>
                
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Aktuelles Unternehmen</Label>
                  <p className="text-sm">
                    {selectedUser.tenant?.name || (
                      <span className="text-muted-foreground">Kein Unternehmen zugeordnet</span>
                    )}
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="tenant-select">Neues Unternehmen</Label>
                  <Select value={selectedTenantId} onValueChange={setSelectedTenantId}>
                    <SelectTrigger>
                      <SelectValue placeholder="Unternehmen auswählen..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Kein Unternehmen (Zuweisung entfernen)</SelectItem>
                      {availableTenants.map((tenant) => (
                        <SelectItem key={tenant.id} value={tenant.id}>
                          {tenant.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            <DialogFooter>
              <Button 
                variant="outline" 
                onClick={() => setIsAssignTenantModalOpen(false)}
                disabled={loading}
              >
                Abbrechen
              </Button>
              <Button 
                onClick={assignUserToTenant}
                disabled={loading || !selectedTenantId}
              >
                {loading ? "Wird zugewiesen..." : "Zuweisen"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  )
}

export default UserManagement